import React, {createContext, useContext, useEffect, useMemo, useState} from 'react';
import {
    INDICATORS,
    TOTAL_IND,
    TOTAL_ITEMS,
    indAnyProposed,
    indReviewState,
    indReviewed,
    revisionComplete,
} from '../data';
import {
    getCpcGovCodeMapService,
    getCpcGovProgressService,
    saveCpcGovReviewDecisionService,
    saveCpcGovRevisionProposalService,
    getCpcGovReviewStateService,
} from '../../../services/cpcGov.service';

const STORAGE_KEY = 'cpcGovernmentReviewState';

/**
 * The revision form is edited as one flat object (see baseForm in
 * IndicatorDetail.page.jsx) but the API stores it structured, so the two shapes
 * are mapped explicitly rather than passed through.
 *
 * Evidence FILES are deliberately not sent — the server discards them, and
 * uploads are API-06.
 */
const toProposalPayload = (indicator_id, item_id, rev) => ({
    indicator_id,
    item_id,
    item_kind: rev.itemKind === 'value' ? 'value' : 'rubric',
    no_score_change: !!rev.noScoreChange,
    proposed_rubric: rev.proposedRubric || null,
    proposed_value: rev.propValue || '',
    proposed_unit: rev.propUnit || '',
    proposed_year: rev.propYear || '',
    proposed_coverage: rev.propCoverage || '',
    reasons: Array.isArray(rev.reasons) ? rev.reasons : [],
    other_reason: rev.otherReason || '',
    evidence: {
        title: rev.evTitle || '',
        issuer: rev.evIssuer || '',
        date: rev.evDate || '',
        jurisdiction: rev.evJurisdiction || '',
        location: rev.evLocation || '',
        excerpt: rev.evExcerpt || '',
        url: rev.evUrl || '',
    },
    legal_status: rev.legalStatus || '',
    implementation_status: rev.implStatus || '',
    proposer: {
        name: rev.pName || '',
        organization: rev.pOrg || '',
        department: rev.pDept || '',
        role: rev.pRole || '',
        email: rev.pEmail || '',
    },
    focal_point: rev.focalPoint || '',
    supervising: rev.supervising || '',
    technical_contact: rev.techContact || '',
});

const emptyState = {
    reviews: {}, // { [indicatorCode]: { [itemCode]: {decision, revision} } }
    approverConfirmed: false,
    confirms: {careful: false, evidence: false, unchanged: false},
    requestSent: false,
};

const DashboardContext = createContext(null);

export const DashboardProvider = ({children}) => {
    const [state, setState] = useState(emptyState);

    /** Surfaced when a decision could not be persisted — see setItemDecision. */
    const [saveError, setSaveError] = useState('');

    /**
     * Hydrate the reviewer's saved decisions from the server, once.
     *
     * sessionStorage still seeds the initial state so the screen paints
     * immediately, but the server is authoritative: a reviewer who signs in from
     * another machine must see the work they already did.
     *
     * Keyed by the indicator's EXTERNAL id, which is what the screens use.
     */
    useEffect(() => {
        let alive = true;
        getCpcGovReviewStateService()
            .then((res) => {
                const server = res?.data?.reviews;
                if (!alive || !server || typeof server !== 'object') return;

                const reviews = {};
                Object.entries(server).forEach(([indicatorId, items]) => {
                    // "CPCI Indicator 080" -> "Indicator 080", the form the URLs use.
                    const key = String(indicatorId).replace(/^CPCI\s+/i, '');
                    reviews[key] = {};
                    Object.entries(items || {}).forEach(([itemId, v]) => {
                        reviews[key][itemId] = {
                            decision: v.decision,
                            revision: v.revision || null,
                        };
                    });
                });

                setState((s) => ({...s, reviews}));
            })
            .catch(() => { /* offline or signed out — the local copy stands */ });
        return () => { alive = false; };
    }, []);

    /**
     * External display names for the hierarchical codes (S1, T05, FC-05 ...),
     * served from the CPC-owned `external_code_map` collection.
     *
     * Fetched ONCE here when the dashboard mounts, exactly as the ML side asked
     * (Seong Min, 2026-08-04) — pages read it from context instead of calling
     * per label. It lives in the database rather than in frontend constants so a
     * wording change requested by Dr. Park needs no frontend redeploy.
     */
    const [codeMap, setCodeMap] = useState({});

    useEffect(() => {
        let alive = true;
        getCpcGovCodeMapService()
            .then((res) => {
                const map = res?.data?.code_map;
                if (alive && map && typeof map === 'object') setCodeMap(map);
            })
            .catch(() => { /* labels fall back to the raw code — see codeName() */ });
        return () => { alive = false; };
    }, []);

    /**
     * Review progress for the whole country (API-10).
     *
     * Fetched rather than derived, because the denominator is not knowable on the
     * client: the real country has 85 Government indicators and 223 assessment
     * items, while the bundled INDICATORS sample has a handful. Deriving locally
     * reported a percentage of the sample and made the screen look wrong.
     *
     * `null` until it arrives; the local derivation below is the fallback.
     */
    const [apiProgress, setApiProgress] = useState(null);

    /**
     * Refetch when the number of recorded decisions changes — not when the
     * `reviews` OBJECT changes identity.
     *
     * `state.reviews` is rebuilt on hydration from sessionStorage and again when
     * the server state lands, so depending on it directly fired this three times
     * per page load, on every page, for a figure only one screen shows. Counting
     * the decisions gives a primitive that changes exactly when the progress
     * figure could have changed.
     */
    const decisionCount = useMemo(
        () => Object.values(state.reviews || {})
            .reduce((n, items) => n + Object.values(items || {}).filter((r) => r && r.decision).length, 0),
        [state.reviews],
    );

    useEffect(() => {
        let alive = true;
        getCpcGovProgressService()
            .then((res) => {
                if (alive && res?.data) setApiProgress(res.data);
            })
            .catch(() => { /* fall back to the locally derived counts */ });
        return () => { alive = false; };
    }, [decisionCount]);

    /**
     * Look up a code's external display name.
     * Falls back to the code itself, so a missing or not-yet-loaded map degrades
     * to showing "FC-05" rather than an empty cell.
     */
    const codeName = (type, code) => codeMap?.[type]?.[code]?.name || code || '';

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            if (raw) setState({...emptyState, ...JSON.parse(raw)});
        } catch (_) {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (_) {
            /* ignore */
        }
    }, [state]);

    /**
     * Record a decision on one item, and persist it (API-04 / API-05).
     *
     * Optimistic: local state updates first so the button responds immediately,
     * then the write goes out. A reviewer works through dozens of items, and
     * blocking each click on a round trip would make the screen feel broken.
     *
     * If the write fails the local state is rolled back and the error is surfaced
     * through `saveError` — silently keeping a decision the server never stored
     * would let someone believe their review was recorded when it was not.
     *
     * `indCode` is the indicator's EXTERNAL id ("Indicator 080"); the API accepts
     * either that or the canonical form and resolves it.
     */
    const setItemDecision = (indCode, itemCode, decision, revision) => {
        let rollback = null;

        setState((s) => {
            const prev = s.reviews[indCode]?.[itemCode];
            rollback = {indCode, itemCode, prev: prev || null};
            return {
                ...s,
                reviews: {
                    ...s.reviews,
                    [indCode]: {
                        ...s.reviews[indCode],
                        [itemCode]: {decision, revision: revision || prev?.revision || null},
                    },
                },
            };
        });

        const undo = () => setState((s) => ({
            ...s,
            reviews: {
                ...s.reviews,
                [indCode]: {...s.reviews[indCode], [itemCode]: rollback && rollback.prev},
            },
        }));

        // A proposal carries a payload; an agree does not.
        const write = revision
            ? saveCpcGovRevisionProposalService(toProposalPayload(indCode, itemCode, revision))
            : saveCpcGovReviewDecisionService(indCode, itemCode, decision === 'agree' ? 'agree' : 'propose');

        return write
            .then((res) => {
                setSaveError('');
                return res;
            })
            .catch((err) => {
                undo();
                setSaveError(err?.message || 'Could not save your decision. Please try again.');
                throw err;
            });
    };

    const setApproverConfirmed = (v) => setState((s) => ({...s, approverConfirmed: v}));
    const toggleConfirm = (key) => setState((s) => ({...s, confirms: {...s.confirms, [key]: !s.confirms[key]}}));
    const setRequestSent = (v) => setState((s) => ({...s, requestSent: v}));

    const progress = useMemo(() => {
        let indReviewedN = 0;
        let indAgreed = 0;
        let indProposed = 0;
        let itemsReviewed = 0;
        let itemsAgreed = 0;
        let itemsProposed = 0;
        let itemsComplete = 0;
        let sampleItems = 0;

        INDICATORS.forEach((ind) => {
            const st = indReviewState(ind, state.reviews);
            if (st !== 'none') {
                indReviewedN += 1;
                if (st === 'agreed') indAgreed += 1; else indProposed += 1;
            }
            ind.items.forEach((it) => {
                sampleItems += 1;
                const r = state.reviews[ind.code]?.[it.code];
                if (r && r.decision) {
                    itemsReviewed += 1;
                    if (r.decision === 'agree') {
                        itemsAgreed += 1;
                    } else {
                        itemsProposed += 1;
                        if (revisionComplete(r.revision)) itemsComplete += 1;
                    }
                }
            });
        });

        const pct = sampleItems ? Math.round((itemsReviewed / sampleItems) * 100) : 0;
        const started = itemsReviewed > 0;
        const evidenceOk = itemsProposed === itemsComplete;
        // All four submission checkboxes — the three self-attestations plus the
        // authorized-approver confirmation — must be checked, not just the last one.
        const allConfirmed = state.approverConfirmed && state.confirms.careful && state.confirms.evidence && state.confirms.unchanged;
        const readinessKey = !started
            ? 'notStarted'
            : !evidenceOk
                ? 'inProgress'
                : allConfirmed
                    ? 'cpci'
                    : 'internal';
        const ready = started && evidenceOk && allConfirmed;

        const local = {
            indReviewed: indReviewedN, indAgreed, indProposed,
            itemsReviewed, itemsAgreed, itemsProposed, itemsComplete,
            pct, TOTAL_IND, TOTAL_ITEMS, started, evidenceOk, readinessKey, ready,
            from_api: false,
        };

        if (!apiProgress) return local;

        /**
         * The server's counts win. They are computed against the country's real
         * 85 indicators and 223 items, which is the denominator the reviewer is
         * actually being measured against.
         *
         * `approverConfirmed` stays local: it is a checkbox on this screen, and
         * nothing records it server-side until API-07 (Submit for Validation)
         * exists. So the last readiness stage is still driven from here.
         */
        const a = apiProgress;
        const apiStarted = a.items_decided > 0;
        const apiEvidenceOk = a.proposals_incomplete === 0;
        const indicatorsReviewed = (a.indicators || []).filter((i) => i.items_decided > 0).length;

        return {
            ...local,
            from_api: true,

            indReviewed: indicatorsReviewed,
            TOTAL_IND: a.indicators_total,
            // Server-side indicator-level agree/propose does not exist yet; the
            // item-level split below is the honest number.
            indAgreed: a.indicators_complete,
            indProposed: Math.max(0, indicatorsReviewed - a.indicators_complete),

            itemsReviewed: a.items_decided,
            TOTAL_ITEMS: a.items_total,
            itemsAgreed: a.agreed,
            itemsProposed: a.proposed,
            itemsComplete: a.proposals_complete,

            pct: a.percent_complete,
            started: apiStarted,
            evidenceOk: apiEvidenceOk,
            readinessKey: !apiStarted
                ? 'notStarted'
                : !apiEvidenceOk
                    ? 'inProgress'
                    : allConfirmed ? 'cpci' : 'internal',
            ready: apiStarted && apiEvidenceOk && allConfirmed,

            // False until API-07 exists — the screen greys the last step out.
            submissionAvailable: !!a.submission_available,
        };
    }, [state.reviews, state.approverConfirmed, state.confirms, apiProgress]);

    const value = {
        reviews: state.reviews,
        approverConfirmed: state.approverConfirmed,
        confirms: state.confirms,
        requestSent: state.requestSent,
        setItemDecision,
        setApproverConfirmed,
        toggleConfirm,
        setRequestSent,
        saveError,
        codeMap,
        codeName,
        progress,
        indReviewState: (ind) => indReviewState(ind, state.reviews),
        indReviewed: (ind) => indReviewed(ind, state.reviews),
        indAnyProposed: (ind) => indAnyProposed(ind, state.reviews),
        itemDecision: (indCode, itemCode) => state.reviews[indCode]?.[itemCode],
    };

    return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
};

export const useDashboard = () => {
    const ctx = useContext(DashboardContext);
    if (!ctx) throw new Error('useDashboard must be used within DashboardProvider');
    return ctx;
};
